import React from 'react';
import PropTypes from 'prop-types';
import {Form as BsForm} from 'react-bootstrap';
import FormContext from '../../contexts/form-context';
import I18n from '../i18n';
import HelpTooltip from '../help-tooltip';
import FieldChangedIndicator from '../field-changed-indicator';
import get from '../../utils/get';

export default function FormField(props) {
  const {
    name,
    label,
    infoLabel,
    infoLabelPosition,
    groupProps,
    labelProps,
    helpTopic,
    helpProps,
    showChangeIndicator,
    children,
  } = props;
  const formContext = React.useContext(FormContext);
  const {initialValues, values, errors, setFieldValue, touched, handleChange, handleBlur} = formContext;

  return (
    <BsForm.Group {...groupProps}>
      <BsForm.Label {...labelProps}>
        <I18n t={label || `property.${name}`} />
        {showChangeIndicator && (
          <FieldChangedIndicator
            name={name}
            originalValue={get(initialValues, name)}
            value={get(values, name)}
            onRevert={setFieldValue}
          />
        )}
        {helpTopic && <HelpTooltip topic={helpTopic} {...helpProps} />}
      </BsForm.Label>
      {infoLabel && infoLabelPosition === 'above' && (
        <div className="form-label-info above">
          <I18n t={infoLabel} />
        </div>
      )}
      {React.Children.map(children, c =>
        React.cloneElement(c, {name, values, touched, errors, handleChange, handleBlur, setFieldValue}),
      )}
      {infoLabel && infoLabelPosition === 'below' && (
        <div className="form-label-info below">
          <I18n t={infoLabel} />
        </div>
      )}
      <BsForm.Control.Feedback type="invalid">{errors[name]}</BsForm.Control.Feedback>
    </BsForm.Group>
  );
}

FormField.defaultProps = {
  groupProps: {},
  showChangeIndicator: true,
  infoLabelPosition: 'below',
};

FormField.propTypes = {
  /** Field name. Must match the data property name. */
  name: PropTypes.string.isRequired,
  /** An I18n key to use as the field label. If not specified the default is `property.${name}` */
  label: PropTypes.string,
  /** An I18n key to use as an info label. This either between the Label and Input, or below the Input depending on `infoLabelPosition` */
  infoLabel: PropTypes.string,
  /** The position for the infoLabel. "above" places it between Label and Input, "below" places it below the Input. Defaults to "below" */
  infoLabelPosition: PropTypes.oneOf(['above', 'below']),
  /** Additional props to pass to the FormGroup */
  groupProps: PropTypes.object,
  /** Additional props to pass to the FormLabel */
  labelProps: PropTypes.object,
  /** Optionally a help topic for this field. If provided, and help tooltip icon will appear next to the label. */
  helpTopic: PropTypes.string,
  /** Additional props to pass down to the HelpTooltip */
  helpProps: PropTypes.object,
  /** The form control or input field. Typically a react-bootstrap `FormControl`.
   * Will be passed the additional props: `{name, values, touched, errors, changed, hasChanges, handleChange, handleBlur}`
   */
  children: PropTypes.node.isRequired,
  /** Whether to show a "value has changed" indicator on this field. Defaults to true. */
  showChangeIndicator: PropTypes.bool,
};
